import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  Search,
  Filter,
  Download,
  Eye,
  CheckCircle,
  XCircle,
  Clock,
  Mail,
  Phone,
  Shield,
  Unlock,
  UserX,
  Users,
  Trash2,
} from 'lucide-react';
import { Card } from '@components/common/Card';
import { Badge } from '@components/common/Badge';
import { Button } from '@components/common/Button';
import { Input } from '@components/forms/Input';
import { mockApplicants } from '@mocks/applicants';
import type { MockApplicant } from '@mocks/applicants';
import { mockApplications } from '@mocks/licenses';
import { LICENSE_CATEGORY_LABELS } from '@utils/constants';

export const ApplicantList: React.FC = () => {
  const navigate = useNavigate();
  const [applicants, setApplicants] = useState<MockApplicant[]>(mockApplicants);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [filterAccountStatus, setFilterAccountStatus] = useState<string>('all');
  const [filterApplicationType, setFilterApplicationType] = useState<string>('all');
  const [sortBy, setSortBy] = useState<'name' | 'date'>('date');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // Combine applicant data with their applications
  const applicantsWithApps = useMemo(() => {
    return applicants.map((applicant) => {
      const application = mockApplications.find((app) => app.applicant_id === applicant.id);
      return { ...applicant, application };
    });
  }, [applicants]);

  // Filter and search
  const filteredApplicants = useMemo(() => {
    let filtered = applicantsWithApps;

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(
        (a) =>
          a.first_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          a.last_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          a.nin.includes(searchTerm) ||
          a.email?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // NIN Status filter
    if (filterStatus !== 'all') {
      filtered = filtered.filter((a) => a.nin_status === filterStatus);
    }

    // Account Status filter
    if (filterAccountStatus !== 'all') {
      filtered = filtered.filter((a) => a.account_status === filterAccountStatus);
    }

    // Application Type filter (NEW/RENEWAL)
    if (filterApplicationType !== 'all') {
      filtered = filtered.filter((a) => a.application?.application_type === filterApplicationType);
    }

    // Sort
    if (sortBy === 'name') {
      filtered.sort((a, b) => a.last_name.localeCompare(b.last_name));
    } else {
      filtered.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
    }

    return filtered;
  }, [applicantsWithApps, searchTerm, filterStatus, filterAccountStatus, sortBy]);

  // Pagination
  const totalPages = Math.ceil(filteredApplicants.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedApplicants = filteredApplicants.slice(startIndex, endIndex);

  // Reset to page 1 when filters change
  React.useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filterStatus, filterAccountStatus, filterApplicationType]);

  const getNINStatusBadge = (status: string) => {
    switch (status) {
      case 'VERIFIED':
        return <Badge variant="success" icon={<CheckCircle className="h-3 w-3" />}>Verified</Badge>;
      case 'PENDING':
        return <Badge variant="warning" icon={<Clock className="h-3 w-3" />}>Pending</Badge>;
      case 'NO_MATCH':
        return <Badge variant="error" icon={<XCircle className="h-3 w-3" />}>No Match</Badge>;
      default:
        return <Badge variant="default">{status}</Badge>;
    }
  };

  const getAccountStatusBadge = (status: string) => {
    switch (status) {
      case 'ACTIVE':
        return <Badge variant="success" icon={<CheckCircle className="h-3 w-3" />}>Active</Badge>;
      case 'SUSPENDED':
        return <Badge variant="warning" icon={<Clock className="h-3 w-3" />}>Suspended</Badge>;
      default:
        return <Badge variant="default">{status}</Badge>;
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const handleSuspend = (applicantId: number) => {
    if (window.confirm('Are you sure you want to suspend this applicant account? They will not be able to log in.')) {
      setApplicants(
        applicants.map((a) => (a.id === applicantId ? { ...a, account_status: 'SUSPENDED' as const } : a))
      );
      toast.success('Applicant account suspended');
      console.log('🔒 Mock: Suspended applicant', applicantId);
    }
  };

  const handleDelete = (applicantId: number) => {
    if (window.confirm('Are you sure you want to DELETE this applicant account? This action cannot be undone and will permanently remove all their data.')) {
      setApplicants(applicants.filter((a) => a.id !== applicantId));
      toast.error('Applicant account deleted permanently');
      console.log('🗑️ Mock: Deleted applicant', applicantId);
    }
  };

  const handleActivate = (applicantId: number) => {
    setApplicants(
      applicants.map((a) => (a.id === applicantId ? { ...a, account_status: 'ACTIVE' as const } : a))
    );
    toast.success('Applicant account activated');
    console.log('✅ Mock: Activated applicant', applicantId);
  };

  const handleExport = () => {
    console.log('✅ Mock: Export applicants data');
    toast.info('Exporting applicants data...');
  };

  // Stats
  const stats = {
    total: applicants.length,
    active: applicants.filter((a) => a.account_status === 'ACTIVE').length,
    suspended: applicants.filter((a) => a.account_status === 'SUSPENDED').length,
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <div>
            <h1 className="text-3xl font-bold text-ncaa-dark">Applicant Management</h1>
            <p className="text-ncaa-medium mt-1">Manage applicant accounts and access control</p>
          </div>
          <Button onClick={handleExport} variant="outline" icon={<Download className="h-4 w-4" />}>
            Export
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
        <Card>
          <div className="flex items-center gap-4">
            <div className="p-3 bg-blue-100 rounded-lg">
              <Users className="h-6 w-6 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Total Applicants</p>
              <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
            </div>
          </div>
        </Card>
        <Card>
          <div className="flex items-center gap-4">
            <div className="p-3 bg-green-100 rounded-lg">
              <CheckCircle className="h-6 w-6 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Active</p>
              <p className="text-2xl font-bold text-gray-900">{stats.active}</p>
            </div>
          </div>
        </Card>
        <Card>
          <div className="flex items-center gap-4">
            <div className="p-3 bg-yellow-100 rounded-lg">
              <Clock className="h-6 w-6 text-yellow-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Suspended</p>
              <p className="text-2xl font-bold text-gray-900">{stats.suspended}</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Filters and Search */}
      <Card className="mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          {/* Search */}
          <div className="md:col-span-2">
            <Input
              placeholder="Search by name, NIN, or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              icon={<Search className="h-4 w-4" />}
            />
          </div>

          {/* NIN Status Filter */}
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ncaa-primary focus:border-transparent"
          >
            <option value="all">All NIN Status</option>
            <option value="VERIFIED">Verified</option>
            <option value="PENDING">Pending</option>
            <option value="NO_MATCH">No Match</option>
          </select>

          {/* Account Status Filter */}
          <select
            value={filterAccountStatus}
            onChange={(e) => setFilterAccountStatus(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ncaa-primary focus:border-transparent"
          >
            <option value="all">All Account Status</option>
            <option value="ACTIVE">Active Only</option>
            <option value="SUSPENDED">Suspended Only</option>
          </select>

          {/* Application Type Filter */}
          <select
            value={filterApplicationType}
            onChange={(e) => setFilterApplicationType(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ncaa-primary focus:border-transparent"
          >
            <option value="all">All Application Types</option>
            <option value="NEW">New License</option>
            <option value="RENEWAL">Renewal</option>
          </select>
        </div>

        <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-200">
          <p className="text-sm text-gray-600">
            Showing <strong>{filteredApplicants.length}</strong> of <strong>{applicantsWithApps.length}</strong> applicants
          </p>
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-600">Sort by:</span>
            <button
              onClick={() => setSortBy('name')}
              className={`px-3 py-1 text-sm rounded ${sortBy === 'name' ? 'bg-ncaa-primary text-white' : 'bg-gray-100'}`}
            >
              Name
            </button>
            <button
              onClick={() => setSortBy('date')}
              className={`px-3 py-1 text-sm rounded ${sortBy === 'date' ? 'bg-ncaa-primary text-white' : 'bg-gray-100'}`}
            >
              Date
            </button>
          </div>
        </div>
      </Card>

      {/* Results Info */}
      {filteredApplicants.length > 0 && (
        <div className="flex items-center justify-between text-sm text-gray-600 mb-4">
          <p>
            Showing {startIndex + 1} to {Math.min(endIndex, filteredApplicants.length)} of{' '}
            {filteredApplicants.length} applicants
          </p>
        </div>
      )}

      {/* Applicants Table */}
      <Card>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Applicant</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">NIN</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">License Application</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Contact</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">NIN Status</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Account Status</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Registered</th>
                <th className="text-right py-3 px-4 text-sm font-semibold text-gray-700">Actions</th>
              </tr>
            </thead>
            <tbody>
              {paginatedApplicants.length === 0 ? (
                <tr>
                  <td colSpan={8} className="text-center py-12">
                    <Users className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500">No applicants found</p>
                  </td>
                </tr>
              ) : (
                paginatedApplicants.map((applicant) => (
                  <tr key={applicant.id} className="border-b border-gray-100 hover:bg-gray-50">
                    {/* Applicant Name */}
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-ncaa-primary text-white rounded-full flex items-center justify-center font-bold">
                          {applicant.first_name[0]}{applicant.last_name[0]}
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">
                            {applicant.first_name} {applicant.last_name}
                          </p>
                          <p className="text-xs text-gray-500">{applicant.nationality}</p>
                        </div>
                      </div>
                    </td>

                    {/* NIN */}
                    <td className="py-3 px-4">
                      <p className="text-sm font-mono text-gray-900">{applicant.nin}</p>
                    </td>

                    {/* License Application */}
                    <td className="py-3 px-4">
                      {applicant.application ? (
                        <div className="space-y-1">
                          <div className="flex items-center gap-2">
                            <Badge 
                              variant={applicant.application.application_type === 'NEW' ? 'info' : 'warning'}
                              size="sm"
                            >
                              {applicant.application.application_type === 'NEW' ? 'New License' : 'Renewal'}
                            </Badge>
                          </div>
                          <p className="text-xs text-gray-600">
                            {LICENSE_CATEGORY_LABELS[applicant.application.license_category]}
                          </p>
                        </div>
                      ) : (
                        <p className="text-xs text-gray-400">No application</p>
                      )}
                    </td>

                    {/* Contact */}
                    <td className="py-3 px-4">
                      <div className="space-y-1">
                        <p className="text-sm text-gray-900 flex items-center gap-1">
                          <Mail className="h-3 w-3 text-gray-400" />
                          {applicant.email}
                        </p>
                        <p className="text-xs text-gray-600 flex items-center gap-1">
                          <Phone className="h-3 w-3 text-gray-400" />
                          {applicant.phone}
                        </p>
                      </div>
                    </td>

                    {/* NIN Status */}
                    <td className="py-3 px-4">{getNINStatusBadge(applicant.nin_status)}</td>

                    {/* Account Status */}
                    <td className="py-3 px-4">{getAccountStatusBadge(applicant.account_status)}</td>

                    {/* Registered Date */}
                    <td className="py-3 px-4">
                      <p className="text-sm text-gray-600">{formatDate(applicant.created_at)}</p>
                    </td>

                    {/* Actions */}
                    <td className="py-3 px-4">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => navigate(`/admin/applicants/${applicant.id}`)}
                          icon={<Eye className="h-3 w-3" />}
                        >
                          View
                        </Button>
                        {applicant.account_status === 'ACTIVE' && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleSuspend(applicant.id)}
                            icon={<UserX className="h-3 w-3" />}
                            className="text-yellow-600 hover:bg-yellow-50"
                          >
                            Suspend
                          </Button>
                        )}
                        {applicant.account_status === 'SUSPENDED' && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleActivate(applicant.id)}
                            icon={<Unlock className="h-3 w-3" />}
                            className="text-green-600 hover:bg-green-50"
                          >
                            Activate
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleDelete(applicant.id)}
                          icon={<Trash2 className="h-3 w-3" />}
                          className="text-red-600 hover:bg-red-50"
                        >
                          Delete
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 mt-6">
          <Button
            size="sm"
            variant="outline"
            onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
            disabled={currentPage === 1}
          >
            Previous
          </Button>

          <div className="flex items-center gap-2">
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => {
              const showPage =
                page === 1 ||
                page === totalPages ||
                (page >= currentPage - 1 && page <= currentPage + 1);

              const showEllipsisBefore = page === currentPage - 2 && currentPage > 3;
              const showEllipsisAfter = page === currentPage + 2 && currentPage < totalPages - 2;

              if (showEllipsisBefore || showEllipsisAfter) {
                return (
                  <span key={page} className="px-2 text-gray-400">
                    ...
                  </span>
                );
              }

              if (!showPage) return null;

              return (
                <button
                  key={page}
                  onClick={() => setCurrentPage(page)}
                  className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${
                    currentPage === page
                      ? 'bg-ncaa-primary text-white'
                      : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  {page}
                </button>
              );
            })}
          </div>

          <Button
            size="sm"
            variant="outline"
            onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
            disabled={currentPage === totalPages}
          >
            Next
          </Button>
        </div>
      )}
    </div>
  );
};
